import React, { useCallback, useEffect, useState } from "react";
import { ScrollText, Loader2, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

// Suggests today's date as the next version, in the same YYYY-MM-DD format.
const todayVersion = () => new Date().toISOString().slice(0, 10);

export default function AdminLegalVersions() {
    const [docs, setDocs] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [target, setTarget] = useState(null);
    const [version, setVersion] = useState(todayVersion());
    const [changelog, setChangelog] = useState("");
    const [submitting, setSubmitting] = useState(false);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const [{ data: v }, { data: h }] = await Promise.all([
                api.get("/legal/versions"),
                api.get("/admin/legal/publish-history"),
            ]);
            setDocs(v.docs || []);
            setHistory(h || []);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const open = (doc) => {
        setTarget(doc);
        setVersion(todayVersion());
        setChangelog("");
    };

    const publish = async () => {
        if (!target || !version.trim()) return;
        try {
            setSubmitting(true);
            await api.post("/admin/legal/publish-version", {
                doc_id: target.id,
                version: version.trim(),
                changelog: changelog.trim(),
            });
            toast.success(`Published ${target.title} @ ${version.trim()} — all prior acceptances are now invalidated.`);
            setTarget(null);
            load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid="admin-legal-versions">
            <div className="mb-6">
                <p className="overline">Command Center · Legal</p>
                <h1 data-testid="dash-page-title" className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">
                    Legal Doc Versions
                </h1>
                <p className="text-sm text-muted-foreground mt-1">
                    Publish a new version of Terms, Privacy, or the Verification &amp; AI Consent Policy. Publishing immediately invalidates all prior user acceptances for that document — every returning user will be prompted with an interstitial to re-consent.
                </p>
            </div>

            {loading ? (
                <div className="py-16 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
            ) : (
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                    {docs.map((d) => (
                        <div key={d.id} className="card-soft p-5" data-testid={`legal-doc-${d.id}`}>
                            <div className="flex items-start justify-between gap-2">
                                <div className="min-w-0">
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{d.id}</div>
                                    <div className="mt-0.5 font-heading font-semibold text-foreground">{d.title}</div>
                                </div>
                                <span className="mono-score font-bold text-emerald-600 text-sm" data-testid={`legal-version-${d.id}`}>{d.version}</span>
                            </div>
                            {d.published_at && (
                                <div className="mt-2 text-[10px] text-muted-foreground">
                                    Last published {new Date(d.published_at).toLocaleString()}
                                </div>
                            )}
                            {d.changelog && (
                                <div className="mt-2 rounded-lg bg-emerald-500/5 border border-emerald-500/20 p-2 text-[11px] text-foreground">
                                    <span className="font-semibold text-emerald-700 dark:text-emerald-400">Changelog:</span> {d.changelog}
                                </div>
                            )}
                            <button
                                onClick={() => open(d)}
                                className="btn-green mt-4 w-full inline-flex items-center justify-center gap-1 text-sm"
                                data-testid={`legal-publish-${d.id}`}
                            >
                                Publish new version <ArrowRight size={13} />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {history.length > 0 && (
                <div className="mt-8">
                    <h2 className="font-heading font-medium text-lg text-foreground mb-3">Publish history</h2>
                    <div className="card-soft overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-border text-left text-[11px] uppercase text-muted-foreground">
                                    <th className="p-3 font-semibold">Doc</th>
                                    <th className="p-3 font-semibold">Version</th>
                                    <th className="p-3 font-semibold">Published by</th>
                                    <th className="p-3 font-semibold">Published at</th>
                                    <th className="p-3 font-semibold">Changelog</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map((h, i) => (
                                    <tr key={`${h.doc_id}-${h.version}-${i}`} className="border-b border-border/60 last:border-0 hover:bg-muted/30">
                                        <td className="p-3 text-xs mono-score">{h.doc_id}</td>
                                        <td className="p-3 text-xs mono-score text-emerald-600">{h.version}</td>
                                        <td className="p-3 text-xs">{h.published_by || "—"}</td>
                                        <td className="p-3 text-xs text-muted-foreground whitespace-nowrap">{h.published_at ? new Date(h.published_at).toLocaleString() : "—"}</td>
                                        <td className="p-3 text-xs text-muted-foreground max-w-[360px] truncate" title={h.changelog}>{h.changelog || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Dialog open={!!target} onOpenChange={(o) => { if (!o) setTarget(null); }}>
                <DialogContent data-testid="legal-publish-dialog">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                            <ScrollText size={16} className="text-emerald-500" /> Publish new version — {target?.title}
                        </DialogTitle>
                        <DialogDescription>
                            This will immediately invalidate every prior acceptance of <b className="text-foreground">{target?.title}</b>. All returning users will be prompted with an interstitial to re-consent before they can continue using gated features.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Version</label>
                            <Input value={version} onChange={(e) => setVersion(e.target.value)} placeholder="YYYY-MM-DD or semver" className="mt-1" data-testid="legal-publish-version" />
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-wider text-muted-foreground font-semibold">Changelog (optional — shown to users in the interstitial)</label>
                            <Textarea value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="e.g. Clarified refund policy for annual plans; added California addendum." rows={4} className="mt-1" data-testid="legal-publish-changelog" />
                        </div>
                    </div>
                    <DialogFooter>
                        <button onClick={() => setTarget(null)} className="btn-outline text-sm px-4 py-2">Cancel</button>
                        <button
                            onClick={publish}
                            disabled={submitting || !version.trim()}
                            className="btn-green text-sm px-4 py-2 inline-flex items-center gap-1 disabled:opacity-60"
                            data-testid="legal-publish-confirm"
                        >
                            {submitting ? <Loader2 size={13} className="animate-spin" /> : <ScrollText size={13} />} Publish &amp; invalidate prior consents
                        </button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
